import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Modal from '@/components/ui/Modal';
import UserSelect from '@/components/admin/common/UserSelect';
import { faBell, faSpinner } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError, showSuccessToast } from '@/utils/toasts';
import type { User } from '@/models';

interface SendTestPushNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSent: () => void;
}

export default function SendTestPushNotificationModal({ isOpen, onClose, onSent }: SendTestPushNotificationModalProps) {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);

  const userHasToken = selectedUser !== null && selectedUser.firebaseUID !== null;
  const isFormValid = userHasToken && title.trim() !== '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedUser === null || !isFormValid) {
      return;
    }

    setLoading(true);

    try {
      await APIRoutes.sendPushNotification({
        userID: selectedUser.id,
        title: title.trim(),
        text: text.trim(),
      });
      showSuccessToast('Test-Push-Benachrichtigung wurde in die Warteschlange eingereiht');
      onSent();
      onClose();

      setSelectedUser(null);
      setTitle('');
      setText('');
    } catch (err) {
      handleAPIError(err, 'Fehler beim Senden der Test-Push-Benachrichtigung');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Test-Push-Benachrichtigung senden"
      icon={<FontAwesomeIcon icon={faBell} className="w-5 h-5 text-purple-600" />}
      closeDisabled={loading}
      maxWidth="lg"
    >
      <form onSubmit={handleSubmit} className="p-6">
        <div className="space-y-4">
          <UserSelect
            id="pushUser"
            label="Empfänger *"
            selectedUser={selectedUser}
            onChange={setSelectedUser}
          />

          {selectedUser !== null && selectedUser.firebaseUID === null && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                Dieser Nutzer hat keinen Push-Token (App nicht installiert oder Push-Benachrichtigungen deaktiviert).
              </p>
            </div>
          )}

          <div>
            <label htmlFor="pushTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Titel *
            </label>
            <input
              type="text"
              id="pushTitle"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Benachrichtigungstitel"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor="pushText" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Nachrichteninhalt
            </label>
            <textarea
              id="pushText"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-vertical"
              placeholder="Test-Nachricht von BolzAI"
              disabled={loading}
            />
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Die Push-Benachrichtigung wird in die Warteschlange eingereiht und automatisch an die App des Nutzers versendet.
              Sie können den Status in der Liste verfolgen.
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || !isFormValid}
            className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 cursor-pointer"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                <span>Wird gesendet...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faBell} className="w-4 h-4" />
                <span>Push-Benachrichtigung senden</span>
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
